import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

import {
  enregistrerConfigIntervalsIcu,
  obtenirConfigIntervalsIcu,
  supprimerConfigIntervalsIcu,
  type ConfigIntervalsIcu,
} from '../Planning/intervalsIcuApi.ts';

// Compte intervals.icu de l'utilisateur (page Mes preferences) : identifiant
// d'athlete et cle API personnelle, pour envoyer les seances du calendrier
// vers intervals.icu (puis la montre). La cle n'est jamais reaffichee.
export default function IntervalsIcuEditeur() {
  const [config, setConfig] = React.useState<ConfigIntervalsIcu | null>(null);
  const [athleteId, setAthleteId] = React.useState('');
  const [apiKey, setApiKey] = React.useState('');
  const [message, setMessage] = React.useState<{ severity: 'success' | 'error'; texte: string } | null>(null);
  const [enCours, setEnCours] = React.useState(false);

  React.useEffect(() => {
    obtenirConfigIntervalsIcu()
      .then((c: ConfigIntervalsIcu) => {
        setConfig(c);
        setAthleteId(c.athleteId ?? '');
      })
      .catch((e: Error) => setMessage({ severity: 'error', texte: e.message }));
  }, []);

  const enregistrer = () => {
    setEnCours(true);
    setMessage(null);
    enregistrerConfigIntervalsIcu(athleteId, apiKey)
      .then((c: ConfigIntervalsIcu) => {
        setConfig(c);
        setAthleteId(c.athleteId ?? '');
        setApiKey('');
        setMessage({ severity: 'success', texte: 'Compte intervals.icu enregistré.' });
      })
      .catch((e: Error) => setMessage({ severity: 'error', texte: e.message }))
      .finally(() => setEnCours(false));
  };

  const supprimer = () => {
    if (!window.confirm('Oublier ton compte intervals.icu (identifiant et clé API) ?')) return;
    setEnCours(true);
    supprimerConfigIntervalsIcu()
      .then(() => {
        setConfig({ athleteId: null, cleConfiguree: false, finCle: null });
        setAthleteId('');
        setApiKey('');
        setMessage({ severity: 'success', texte: 'Compte intervals.icu oublié.' });
      })
      .catch((e: Error) => setMessage({ severity: 'error', texte: e.message }))
      .finally(() => setEnCours(false));
  };

  return (
    <Box id="preference-intervals-icu">
      <h2>intervals.icu</h2>
      <Typography sx={{ mb: 2 }} color="text.secondary">
        Pour envoyer les séances de ton calendrier vers{' '}
        <Link href="https://intervals.icu" target="_blank" rel="noopener noreferrer">intervals.icu</Link>, qui les
        synchronise ensuite avec ta montre (Garmin…). Ton identifiant d'athlète et ta clé API se trouvent dans
        intervals.icu, menu <b>Settings</b>, section <b>Developer Settings</b> (l'identifiant ressemble à{' '}
        <code>i12345</code>). La clé reste privée : elle n'est jamais réaffichée ici.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 420 }}>
        <TextField
          label="Identifiant d'athlète"
          placeholder="i12345"
          value={athleteId}
          onChange={(e) => setAthleteId(e.target.value)}
        />
        <TextField
          label="Clé API"
          type="password"
          autoComplete="off"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder={config?.cleConfiguree ? `•••• ${config.finCle ?? ''} (laisser vide pour la garder)` : ''}
          helperText={config?.cleConfiguree ? 'Une clé est enregistrée.' : 'Aucune clé enregistrée.'}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            onClick={enregistrer}
            disabled={enCours || !athleteId.trim() || (!apiKey.trim() && !config?.cleConfiguree)}
          >
            Enregistrer
          </Button>
          {config?.cleConfiguree && (
            <Button color="error" onClick={supprimer} disabled={enCours}>Oublier ce compte</Button>
          )}
        </Box>
        {message && <Alert severity={message.severity}>{message.texte}</Alert>}
      </Box>
    </Box>
  );
}
